import { parse } from '@atlassian/cs-ari';
import { getExtensionType } from '@atlassian/forge-ui';
import type { ForgeUIExtensionType } from '@atlassian/forge-ui-types';
import { convertPatternToRegex } from './utils/convertPatternToRegex';
import { isPatternValid } from './utils/isPatternValid';
import type { AutoConvertDefinition, AutoConvertMacroAttributesMap } from './types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	EXTENSION_NAMESPACE,
	FORGE_INLINE_BODIED_PARAM,
	MACRO_OUTPUT_TYPE_PARAM,
} from '../../utils/constants';
import type { ForgeEmbeddedMacroContext } from '../../types/extension';

export const createAutoConvertDefinitions = (
	extensions: readonly ForgeUIExtensionType[] | null,
	embeddedMacroContext: ForgeEmbeddedMacroContext,
): {
	autoConvertDefinitions: AutoConvertDefinition[] | undefined;
	macroADFNodeMap: AutoConvertMacroAttributesMap | undefined;
} => {
	if (!extensions) {
		return { autoConvertDefinitions: undefined, macroADFNodeMap: undefined };
	}

	const autoConvertDefinitions: AutoConvertDefinition[] = [];

	// a map of macro key to ADF node
	const macroADFNodeMap: AutoConvertMacroAttributesMap = {};

	extensions.forEach((extension) => {
		if (!parse(extension.id).resourceId || !extension.properties.autoConvert) {
			return;
		}

		const extensionIdWithoutPrefix = parse(extension.id).resourceId!;
		const isLayoutSwitchEnabled = fg('platform_forge_inline_bodied_layout_switch');
		const isInlineBodied =
			isLayoutSwitchEnabled &&
			extension.properties.layout === 'inline-bodied' &&
			extension.properties.render === 'native' &&
			fg('platform_forge_inline_bodied_macro');

		// only macro with autoconvertData can be converted
		extension.properties.autoConvert.matchers.forEach(({ pattern }) => {
			// Invalid patterns will silently fail. We are relying on the CLI linter to warn developers for invalid pattern
			if (isPatternValid(pattern)) {
				const type = getExtensionType(extension);
				macroADFNodeMap[extensionIdWithoutPrefix] = {
					type: type,
					...(isLayoutSwitchEnabled && extension.properties.layout === 'inline-bodied'
						? { content: [{ type: 'paragraph', content: [] }] }
						: {}),
					attrs: {
						extensionKey: extensionIdWithoutPrefix,
						extensionType: EXTENSION_NAMESPACE,
						parameters: {
							extensionId: extension.id,
							extensionTitle: extension.properties.title,
							layout: extension.properties.layout || 'block',
							forgeEnvironment: extension.environmentType,
							render: extension.properties.render || 'default',
							embeddedMacroContext,
							...(isInlineBodied
								? {
										guestParams: {
											[MACRO_OUTPUT_TYPE_PARAM]: 'INLINE',
											[FORGE_INLINE_BODIED_PARAM]: 'true',
										},
									}
								: {}),
						},
						layout: 'default',
						text: extension.properties.title,
					},
				};
				const autoConvertDefinition: AutoConvertDefinition = {
					macroName: extension.properties.title || '',
					matcher: convertPatternToRegex(pattern),
					extensionKey: extensionIdWithoutPrefix,
				};
				autoConvertDefinitions.push(autoConvertDefinition);
			}
		});
	});
	return { autoConvertDefinitions, macroADFNodeMap };
};
